import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

import {
  Category,
  Company,
  CompanyDetailResponse,
  CompanyLinkedAccount,
  CompanySubscription,
  LocationUsage,
  LoginPayload,
  LoginResponse,
  PlacementType,
  Product,
  ProductQueryOptions,
  ProductSeries,
  RoomTemplate,
  UsageSummaryResponse
} from '../models/admin-api.models';

// Re-export sementara biar import lama dari service tetap aman.
// Nanti pelan-pelan component bisa dipindah import-nya ke models/admin-api.models.
export type {
  Category,
  Company,
  CompanyDetailResponse,
  CompanyLinkedAccount,
  CompanySubscription,
  CompanyUsageSummary,
  LocationUsage,
  LoginPayload,
  LoginResponse,
  PlacementType,
  Product,
  ProductQueryOptions,
  ProductSeries,
  RoomTemplate,
  UsageMetric,
  UsageSummaryResponse
} from '../models/admin-api.models';


type UploadImageEntity =
  | 'category'
  | 'product-series'
  | 'product'
  | 'texture'
  | 'room-template'
  | 'company'
  | 'general';

export type UploadImageExtraFields = Record<string, string | number | boolean | null | undefined>;

export type UploadImageResponse = {
  message?: string;
  entity?: string;
  url?: string;
  image_url?: string;
  image?: string;
  logo_url?: string;
  texture_url?: string;
  relative_url?: string;
  filename?: string;
  storage?: string;
};

export type GeneratedProductTextureResponse = {
  success: boolean;
  partialSuccess?: boolean;
  message: string;
  productId?: number | string;
  sourceImageUrl?: string;
  textures?: Array<{
    id?: number | string | null;
    pattern: string;
    status: 'pending' | 'processing' | 'completed' | 'failed' | string;
    url?: string | null;
    width?: number | null;
    height?: number | null;
    error?: string | null;
  }>;
  errors?: Array<{
    pattern: string;
    message: string;
  }>;
  data?: {
    product_id?: number | string;
    source_image_url?: string;
    texture_url?: string;
    texture_filename?: string;
    category?: string;
    product_series?: string;
    pattern?: string;
    textures?: Array<{
      id?: number | string | null;
      pattern: string;
      status: 'pending' | 'processing' | 'completed' | 'failed' | string;
      url?: string | null;
      width?: number | null;
      height?: number | null;
      error?: string | null;
    }>;
    errors?: Array<{
      pattern: string;
      message: string;
    }>;
  };
  error?: string;
};

@Injectable({
  providedIn: 'root'
})
export class AdminApiService {
  private readonly backendUrl = this.getBackendUrl();
  private readonly apiUrl = this.normalizeBaseUrl(environment.apiUrl || `${this.backendUrl}/api`);
  private readonly visualizerCacheTtlMs = 10 * 60 * 1000;
  private readonly requestCache = new Map<string, Observable<unknown>>();

  constructor(private http: HttpClient) {}

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, payload);
  }

  getCategories(activeOnly = false): Observable<Category[]> {
    let params = new HttpParams();

    if (activeOnly) {
      params = params.set('active_only', '1');
    }

    return this.http.get<Category[]>(`${this.apiUrl}/categories`, { params });
  }

  getCategoriesByPlacement(
    placement: PlacementType,
    activeOnly = false
  ): Observable<Category[]> {
    let params = new HttpParams().set('placement', placement);

    if (activeOnly) {
      params = params.set('active_only', '1');
    }

    return this.http.get<Category[]>(`${this.apiUrl}/categories`, { params });
  }

  getCategoriesByType(type: PlacementType, activeOnly = false): Observable<Category[]> {
    return this.getCategoriesByPlacement(type, activeOnly);
  }

  createCategory(payload: Partial<Category>): Observable<any> {
    return this.http.post(`${this.apiUrl}/categories`, payload);
  }

  updateCategory(id: number, payload: Partial<Category>): Observable<any> {
    return this.http.put(`${this.apiUrl}/categories/${id}`, payload);
  }

  updateCategoryStatus(id: number, isActive: boolean | number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/categories/${id}/status`, {
      is_active: this.toActiveNumber(isActive)
    });
  }

  deleteCategory(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/categories/${id}`);
  }

  getProductSeries(
    options: {
      category_id?: number | string;
      placement?: PlacementType;
      active_only?: boolean;
    } = {}
  ): Observable<ProductSeries[]> {
    let params = new HttpParams();

    if (options.category_id) {
      params = params.set('category_id', String(options.category_id));
    }

    if (options.placement) {
      params = params.set('placement', options.placement);
    }

    if (options.active_only) {
      params = params.set('active_only', '1');
    }

    return this.http.get<ProductSeries[]>(`${this.apiUrl}/product-series`, { params });
  }

  getProductSeriesByCategory(
    categoryId: number,
    activeOnly = false,
    placement?: PlacementType
  ): Observable<ProductSeries[]> {
    return this.getProductSeries({
      category_id: categoryId,
      placement,
      active_only: activeOnly
    }).pipe(
      catchError(() => {
        return this.getProductsByCategory(categoryId).pipe(
          map((products) => this.buildSeriesFromProducts(products, categoryId)),
          catchError(() => of([]))
        );
      })
    );
  }

  getCategorySeries(
    categoryId: number,
    activeOnly = false
  ): Observable<ProductSeries[]> {
    let params = new HttpParams();

    if (activeOnly) {
      params = params.set('active_only', '1');
    }

    return this.http
      .get<ProductSeries[]>(`${this.apiUrl}/categories/${categoryId}/series`, { params })
      .pipe(
        catchError(() => this.getProductSeriesByCategory(categoryId, activeOnly))
      );
  }

  createProductSeries(payload: Partial<ProductSeries>): Observable<any> {
    return this.http.post(`${this.apiUrl}/product-series`, payload);
  }

  updateProductSeries(id: number, payload: Partial<ProductSeries>): Observable<any> {
    return this.http.put(`${this.apiUrl}/product-series/${id}`, payload);
  }

  updateProductSeriesStatus(id: number, isActive: boolean | number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/product-series/${id}/status`, {
      is_active: this.toActiveNumber(isActive)
    });
  }

  deleteProductSeries(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/product-series/${id}`);
  }

  getProducts(options: ProductQueryOptions = {}): Observable<Product[]> {
    let params = new HttpParams();

    if (options.placement) {
      params = params.set('placement', options.placement);
    }

    if (options.category_id) {
      params = params.set('category_id', String(options.category_id));
    }

    if (options.series_id) {
      params = params.set('series_id', String(options.series_id));
    }

    if (options.active_only) {
      params = params.set('active_only', '1');
    }

    return this.http.get<Product[]>(`${this.apiUrl}/products`, { params });
  }

  getActiveProducts(
    options: Omit<ProductQueryOptions, 'active_only'> = {}
  ): Observable<Product[]> {
    const hasOptions = Object.keys(options || {}).length > 0;

    if (hasOptions) {
      return this.getProducts({
        ...options,
        active_only: true
      });
    }

    return this.cachedJsonRequest<Product[]>(
      'active-products',
      () => this.getProducts({ active_only: true })
    );
  }

  getProductsByCategory(categoryId: number, activeOnly = false): Observable<Product[]> {
    return this.getProducts({
      category_id: categoryId,
      active_only: activeOnly
    });
  }

  getProductsBySeries(seriesId: number, activeOnly = false): Observable<Product[]> {
    return this.getProducts({
      series_id: seriesId,
      active_only: activeOnly
    });
  }

  getProductsByPlacement(
    placement: PlacementType,
    activeOnly = false
  ): Observable<Product[]> {
    return this.getProducts({
      placement,
      active_only: activeOnly
    });
  }

  createProduct(payload: Partial<Product>): Observable<any> {
    return this.http.post(`${this.apiUrl}/products`, payload).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  getProductById(id: number): Observable<Product> {
    return this.http.get<Product>(`${this.apiUrl}/products/${id}`);
  }

  updateProduct(id: number, payload: Partial<Product>): Observable<any> {
    return this.http.put(`${this.apiUrl}/products/${id}`, payload).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  updateProductStatus(id: number, isActive: boolean | number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/products/${id}/status`, {
      is_active: this.toActiveNumber(isActive)
    }).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  deleteProduct(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/products/${id}`).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  getRoomTemplates(activeOnly = false): Observable<RoomTemplate[]> {
    if (activeOnly) {
      return this.cachedJsonRequest<RoomTemplate[]>(
        'active-room-templates',
        () => {
          const params = new HttpParams().set('active_only', '1');
          return this.http.get<RoomTemplate[]>(`${this.apiUrl}/room-templates`, { params });
        }
      );
    }

    return this.http.get<RoomTemplate[]>(`${this.apiUrl}/room-templates`);
  }

  getRoomTemplatesByGroup(
    roomGroup: string,
    activeOnly = false
  ): Observable<RoomTemplate[]> {
    let params = new HttpParams().set('room_group', roomGroup);

    if (activeOnly) {
      params = params.set('active_only', '1');
    }

    return this.http.get<RoomTemplate[]>(`${this.apiUrl}/room-templates`, { params });
  }

  getRoomTemplateGroups(activeOnly = false): Observable<string[]> {
    let params = new HttpParams();

    if (activeOnly) {
      params = params.set('active_only', '1');
    }

    return this.http.get<string[]>(`${this.apiUrl}/room-templates/groups`, { params });
  }

  getRoomTemplateById(id: number): Observable<RoomTemplate> {
    return this.http.get<RoomTemplate>(`${this.apiUrl}/room-templates/${id}`);
  }

  createRoomTemplate(payload: Partial<RoomTemplate>): Observable<any> {
    return this.http.post(`${this.apiUrl}/room-templates`, payload).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  updateRoomTemplate(id: number, payload: Partial<RoomTemplate>): Observable<any> {
    return this.http.put(`${this.apiUrl}/room-templates/${id}`, payload).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  updateRoomTemplateStatus(id: number, isActive: boolean | number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/room-templates/${id}/status`, {
      is_active: this.toActiveNumber(isActive)
    }).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  updateRoomTemplateProcessStatus(
    id: number,
    payload: {
      process_status: 'draft' | 'processing' | 'ready' | 'failed' | string;
      process_done?: boolean | number;
      template_dir?: string | null;
      manifest_url?: string | null;
      process_error?: string | null;
    }
  ): Observable<any> {
    return this.http.patch(`${this.apiUrl}/room-templates/${id}/process-status`, payload).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  processRoomTemplate(id: number, wait = false): Observable<any> {
    const params = wait ? new HttpParams().set('wait', '1') : undefined;

    return this.http.post(`${this.apiUrl}/room-templates/${id}/process`, {}, { params }).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  getRoomTemplateManifest(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/room-templates/${id}/manifest`);
  }

  deleteRoomTemplate(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/room-templates/${id}`).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  deleteAllRoomTemplates(ids: number[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/room-templates/delete-many`, { ids }).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  getCompanies(): Observable<Company[]> {
    return this.http.get<Company[]>(`${this.apiUrl}/companies`);
  }

  getCompanyDetail(id = 1): Observable<CompanyDetailResponse> {
    return this.http.get<CompanyDetailResponse>(`${this.apiUrl}/companies/${id}`);
  }

  updateCompany(id: number, payload: Partial<Company>): Observable<any> {
    return this.http.put(`${this.apiUrl}/companies/${id}`, payload);
  }

  uploadCompanyLogo(
    id: number,
    file: File
  ): Observable<UploadImageResponse> {
    const formData = new FormData();
    formData.append('logo', file);

    return this.http.post<UploadImageResponse>(
      `${this.apiUrl}/companies/${id}/logo`,
      formData
    );
  }

  uploadImage(
    entity: UploadImageEntity,
    file: File,
    extraFields: UploadImageExtraFields = {}
  ): Observable<UploadImageResponse> {
    const formData = new FormData();
    formData.append('image', file);

    Object.entries(extraFields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });

    return this.http.post<UploadImageResponse>(
      `${this.apiUrl}/uploads/image/${entity}`,
      formData
    );
  }

  uploadProductWithGeneratedTexture(
    file: File,
    fields: UploadImageExtraFields = {}
  ): Observable<GeneratedProductTextureResponse> {
    const formData = new FormData();
    formData.append('image', file);

    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });

    return this.http.post<GeneratedProductTextureResponse>(
      `${this.apiUrl}/products/upload`,
      formData
    ).pipe(
      tap(() => this.clearVisualizerSourceCache())
    );
  }

  getUploadedUrl(response: UploadImageResponse | null | undefined): string {
    return (
      response?.url ||
      response?.image_url ||
      response?.image ||
      response?.logo_url ||
      response?.texture_url ||
      response?.relative_url ||
      ''
    );
  }

  resolveImageUrl(value?: string | null, fallback = '/uploads/others/no-image.png'): string {
    const imageUrl = String(value || '').trim();

    if (!imageUrl) {
      return this.resolveImageUrl(fallback, '');
    }

    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      const backendAssetUrl = this.rewriteBackendAssetUrl(imageUrl);

      if (backendAssetUrl) {
        return backendAssetUrl;
      }

      return imageUrl;
    }

    if (imageUrl.startsWith('/uploads')) {
      return `${this.backendUrl}${imageUrl}`;
    }

    if (imageUrl.startsWith('uploads/')) {
      return `${this.backendUrl}/${imageUrl}`;
    }

    if (imageUrl.startsWith('/assets/')) {
      return imageUrl.slice(1);
    }

    return imageUrl;
  }

  updateCompanyLinkedAccount(
    id: number,
    payload: Partial<CompanyLinkedAccount>
  ): Observable<any> {
    return this.http.put(`${this.apiUrl}/company-linked-accounts/${id}`, payload);
  }

  createCompanyLinkedAccount(payload: Partial<CompanyLinkedAccount>): Observable<any> {
    return this.http.post(`${this.apiUrl}/company-linked-accounts`, payload);
  }

  updateCompanySubscription(
    id: number,
    payload: Partial<CompanySubscription>
  ): Observable<any> {
    return this.http.put(`${this.apiUrl}/company-subscriptions/${id}`, payload);
  }

  createCompanySubscription(payload: Partial<CompanySubscription>): Observable<any> {
    return this.http.post(`${this.apiUrl}/company-subscriptions`, payload);
  }

  getUsageSummary(companyId?: number): Observable<UsageSummaryResponse> {
    let params = new HttpParams();

    if (companyId) {
      params = params.set('company_id', String(companyId));
    }

    return this.http.get<UsageSummaryResponse>(`${this.apiUrl}/usage-summary`, { params });
  }

  getCompanyLocationUsages(companyId: number): Observable<LocationUsage[]> {
    return this.http.get<LocationUsage[]>(`${this.apiUrl}/companies/${companyId}/location-usages`);
  }

  getProductsEstimator(): Observable<any> {
    return this.http.get(`${this.apiUrl}/product/all`);
  }

  getProductsEstimatorDetail(code: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/product/detail/${code}`);
  }

  clearVisualizerSourceCache(): void {
    this.requestCache.clear();

    try {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('frantinco_visualizer_source_cache:'))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // ignore browser storage errors
    }
  }

  private cachedJsonRequest<T>(key: string, factory: () => Observable<T>): Observable<T> {
    const memoryKey = `frantinco_visualizer_source_cache:${key}`;
    const memory = this.requestCache.get(memoryKey) as Observable<T> | undefined;

    if (memory) {
      return memory;
    }

    const sessionValue = this.readSessionCache<T>(memoryKey);

    if (sessionValue) {
      return of(sessionValue);
    }

    const request$ = factory().pipe(
      tap((value) => this.writeSessionCache(memoryKey, value)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.requestCache.set(memoryKey, request$ as Observable<unknown>);
    return request$;
  }

  private readSessionCache<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(key);

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as { savedAt?: number; value?: T };
      const savedAt = Number(parsed.savedAt || 0);

      if (!savedAt || Date.now() - savedAt > this.visualizerCacheTtlMs) {
        sessionStorage.removeItem(key);
        return null;
      }

      return parsed.value ?? null;
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  }

  private writeSessionCache<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
    } catch {
      // ignore storage quota/private-mode errors
    }
  }

  private normalizeBaseUrl(value: string): string {
    return String(value || '').replace(/\/+$/, '');
  }

  private getBackendUrl(): string {
    const configuredUrl = String(environment.backendUrl || '').trim();

    if (configuredUrl) {
      return this.normalizeBaseUrl(configuredUrl);
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return this.normalizeBaseUrl(window.location.origin);
    }

    return '';
  }

  private rewriteBackendAssetUrl(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      const isLocalBackend =
        parsedUrl.hostname === 'localhost' ||
        parsedUrl.hostname === '127.0.0.1';

      if (isLocalBackend && parsedUrl.pathname.startsWith('/uploads/')) {
        return `${this.backendUrl}${parsedUrl.pathname}${parsedUrl.search}`;
      }
    } catch {
      return null;
    }

    return null;
  }

  private toActiveNumber(value: unknown): number {
    return value === true || value === 1 || value === '1' ? 1 : 0;
  }

  private buildSeriesFromProducts(products: Product[], categoryId: number): ProductSeries[] {
    const mapById = new Map<number, ProductSeries>();

    products.forEach((product) => {
      const seriesId = Number(product.series_id || 0);

      if (!seriesId || mapById.has(seriesId)) {
        return;
      }

      mapById.set(seriesId, {
        id: seriesId,
        category_id: categoryId,
        category_name: product.category_name,
        name: product.series_name || `Series ${seriesId}`,
        slug: product.series_slug || null,
        image: product.series_image || null,
        is_active: product.series_is_active ?? 1
      });

      ([
        'ceiling',
        'floor',
        'wall',
        'backsplash',
        'furniture',
        'toilet',
        'meja',
        'laboratory'
      ] as PlacementType[]).forEach((placement) => {
        const seriesPlacementKey = `series_${placement}` as keyof Product;
        const categoryPlacementValue = product[placement];
        const seriesPlacementValue = product[seriesPlacementKey];

        (mapById.get(seriesId) as ProductSeries)[placement] =
          this.toActiveNumber(seriesPlacementValue ?? categoryPlacementValue ?? 0);
      });
    });

    return Array.from(mapById.values());
  }
}
